import React from 'react'
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer'
import { PAYMENT_STATUS } from '../../types'
import type { TermSaleReportItem } from './termSalesReportUtils'

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  const s = String(v).trim()
  if (s === '') return 0
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return parseFloat(s) || 0
}

const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDateBR = (dateString?: string | null): string => {
  if (!dateString) return '—'
  const d = String(dateString).split('T')[0]
  const [y, m, day] = d.split('-')
  if (!y || !m || !day) return d
  return `${day}/${m}/${y}`
}

const truncate = (v: unknown, max: number): string => {
  const s = String(v ?? '').replace(/\s+/g, ' ').trim()
  if (!s) return '—'
  if (s.length <= max) return s
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`
}

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14, gap: 12, alignItems: 'center' },
  logo: { width: 140, height: 36, objectFit: 'contain' },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#6B7280', fontSize: 9, lineHeight: 1.35 },
  totalsBox: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 10, marginBottom: 14 },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 },
  totalsLabel: { color: '#6B7280' },
  totalsValue: { fontWeight: 700 },
  saleBlock: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  saleHeader: {
    backgroundColor: '#F3F4F6',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  saleTitle: { fontSize: 11, fontWeight: 700 },
  saleMeta: { marginTop: 3, color: '#6B7280', fontSize: 9, lineHeight: 1.35 },
  saleSummary: { flexDirection: 'row', flexWrap: 'wrap', padding: 10, gap: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  summaryItem: { width: '31%', minWidth: 150 },
  summaryLabel: { color: '#6B7280', fontSize: 8 },
  summaryValue: { fontWeight: 700, marginTop: 2 },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    paddingVertical: 6,
    paddingHorizontal: 8,
    alignItems: 'flex-start',
  },
  colPayment: { width: '18%' },
  colValue: { width: '16%', textAlign: 'right' },
  colProfit: { width: '18%', textAlign: 'right' },
  colStatus: { width: '14%' },
  colDate: { width: '18%' },
  colDue: { width: '16%' },
  profitPaid: { color: '#15803D', fontWeight: 700 },
  profitPending: { color: '#6B7280' },
  footerNote: { marginTop: 10, color: '#6B7280', fontSize: 8, lineHeight: 1.35 },
  previewTag: { color: '#B45309', fontSize: 8, marginTop: 4 },
})

export interface TermSalesReportPDFProps {
  items: TermSaleReportItem[]
  generatedAtLabel: string
  logoUrl?: string | null
  filterDescription?: string
}

const TermSalesReportPDF: React.FC<TermSalesReportPDFProps> = ({
  items,
  generatedAtLabel,
  logoUrl,
  filterDescription,
}) => {
  const totalSaleValue = items.reduce((sum, item) => sum + toNum(item.sale.sale_price), 0)
  const totalReceived = items.reduce((sum, item) => sum + item.amountReceived, 0)
  const totalPendingReceive = items.reduce((sum, item) => sum + item.amountPending, 0)
  const totalProfit = items.reduce((sum, item) => sum + item.breakdown.totalProfit, 0)
  const totalProfitRecognized = items.reduce((sum, item) => sum + item.breakdown.recognizedProfit, 0)
  const totalProfitPending = items.reduce((sum, item) => sum + item.breakdown.pendingProfit, 0)

  return (
    <Document title="Vendas a prazo">
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
          <View style={{ flexGrow: 1, paddingHorizontal: 8 }}>
            <Text style={styles.title}>Vendas a prazo</Text>
            <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
            {filterDescription ? <Text style={styles.subtitle}>{filterDescription}</Text> : null}
          </View>
          <View>
            <Text style={styles.subtitle}>{items.length} venda(s)</Text>
          </View>
        </View>

        <View style={styles.totalsBox}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Valor total das vendas:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalSaleValue)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total já recebido:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalReceived)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Saldo a receber:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalPendingReceive)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Lucro total das operações:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalProfit)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Lucro já no caixa:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalProfitRecognized)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Lucro ainda a reconhecer:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalProfitPending)}</Text>
          </View>
        </View>

        {items.map((item) => (
          <View key={item.sale.id} style={styles.saleBlock} wrap>
            <View style={styles.saleHeader}>
              <Text style={styles.saleTitle}>
                Venda #{item.sale.id} — {formatDateBR(item.sale.sale_date)}
              </Text>
              <Text style={styles.saleMeta}>
                Veículo: {truncate(item.vehicleLabel, 80)} | Cliente: {truncate(item.buyerLabel, 50)}
                {item.buyerPhone ? ` | ${truncate(item.buyerPhone, 24)}` : ''}
              </Text>
              <Text style={styles.saleMeta}>
                Valor da venda: {formatCurrency(item.sale.sale_price)} | Entrada:{' '}
                {formatCurrency(item.sale.down_payment)} | Situação:{' '}
                {PAYMENT_STATUS[item.sale.payment_status] ?? item.sale.payment_status}
              </Text>
              {!item.hasPersistedInstallments ? (
                <Text style={styles.previewTag}>
                  Parcelas estimadas (API sem cobranças individuais) — baixas podem não estar refletidas.
                </Text>
              ) : null}
            </View>

            <View style={styles.saleSummary}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Recebido</Text>
                <Text style={styles.summaryValue}>{formatCurrency(item.amountReceived)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>A receber</Text>
                <Text style={styles.summaryValue}>{formatCurrency(item.amountPending)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Lucro no caixa</Text>
                <Text style={styles.summaryValue}>{formatCurrency(item.breakdown.recognizedProfit)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Lucro total</Text>
                <Text style={styles.summaryValue}>{formatCurrency(item.breakdown.totalProfit)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Lucro pendente</Text>
                <Text style={styles.summaryValue}>{formatCurrency(item.breakdown.pendingProfit)}</Text>
              </View>
            </View>

            <View style={styles.tableHeader}>
              <Text style={styles.colPayment}>Pagamento</Text>
              <Text style={styles.colValue}>Valor</Text>
              <Text style={styles.colProfit}>Lucro (caixa)</Text>
              <Text style={styles.colStatus}>Situação</Text>
              <Text style={styles.colDate}>Pago em</Text>
              <Text style={styles.colDue}>Vencimento</Text>
            </View>

            {item.breakdown.rows.map((row) => (
              <View key={row.key} style={styles.tableRow}>
                <Text style={styles.colPayment}>{row.label}</Text>
                <Text style={styles.colValue}>{formatCurrency(row.paymentAmount)}</Text>
                <Text style={[styles.colProfit, row.status === 'paid' ? styles.profitPaid : styles.profitPending]}>
                  {row.status === 'paid'
                    ? formatCurrency(row.profitAmount)
                    : `(${formatCurrency(row.profitAmount)})`}
                </Text>
                <Text style={styles.colStatus}>{row.status === 'paid' ? 'No caixa' : 'Pendente'}</Text>
                <Text style={styles.colDate}>
                  {row.status === 'paid' && row.paidAt ? formatDateBR(row.paidAt) : '—'}
                </Text>
                <Text style={styles.colDue}>{row.dueDate ? formatDateBR(row.dueDate) : '—'}</Text>
              </View>
            ))}
          </View>
        ))}

        <Text style={styles.footerNote}>
          Lucro no caixa = proporção do lucro da operação sobre cada valor recebido. Valores entre parênteses ainda
          não entraram no caixa. Entrada reconhecida na data da venda; parcelas na data da baixa.
        </Text>
      </Page>
    </Document>
  )
}

export default TermSalesReportPDF
